import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix per i marker di default di Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface Occurrence {
  key: number;
  decimalLatitude: number;
  decimalLongitude: number;
  country?: string;
  locality?: string;
  year?: number;
  basisOfRecord?: string;
}

interface SpeciesMapProps {
  usageKey: string;
  scientificName: string;
}

export const SpeciesMap = ({ usageKey, scientificName }: SpeciesMapProps) => {
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOccurrences = async () => {
      try {
        setLoading(true);
        setError(null);

        // Chiamata all'API GBIF per ottenere le occorrenze con coordinate
        const response = await fetch(
          `https://api.gbif.org/v1/occurrence/search?taxonKey=${usageKey}&hasCoordinate=true&limit=300`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch occurrence data");
        }

        const data = await response.json();

        if (data.results && data.results.length > 0) {
          setOccurrences(data.results);
        } else {
          setError("No occurrence data with coordinates found");
        }
      } catch (err) {
        console.error("Error fetching occurrences:", err);
        setError(err instanceof Error ? err.message : "Failed to load map data");
      } finally {
        setLoading(false);
      }
    };

    if (usageKey) {
      fetchOccurrences();
    }
  }, [usageKey]);

  if (loading) {
    return (
      <div className="flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem", color: "var(--button-bg)" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-4" style={{ color: "var(--text-secondary)" }}>
        <i className="pi pi-exclamation-triangle mr-2" />
        {error}
      </div>
    );
  }

  if (occurrences.length === 0) {
    return (
      <div className="text-center p-4" style={{ color: "var(--text-secondary)" }}>
        No occurrence data available
      </div>
    );
  }

  // Calcola il centro della mappa basato sulle occorrenze
  const center: [number, number] = [
    occurrences.reduce((sum, o) => sum + o.decimalLatitude, 0) / occurrences.length,
    occurrences.reduce((sum, o) => sum + o.decimalLongitude, 0) / occurrences.length,
  ];

  return (
    <div style={{ height: "400px", width: "100%", borderRadius: "8px", overflow: "hidden" }}>
      <MapContainer
        center={center}
        zoom={2}
        minZoom={2}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={true}
        worldCopyJump={true}
        maxBounds={[[-90, -180], [90, 180]]}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {occurrences.map((occurrence) => (
          <CircleMarker
            key={occurrence.key}
            center={[occurrence.decimalLatitude, occurrence.decimalLongitude]}
            radius={6}
            pathOptions={{
              fillColor: "#FF960A",
              color: "#329696",
              weight: 2,
              opacity: 0.8,
              fillOpacity: 0.6,
            }}
          >
            <Popup>
              <div style={{ fontFamily: "var(--font-family)", fontSize: "0.9rem" }}>
                <strong style={{ color: "var(--text-outside-buttons)" }}>{scientificName}</strong>
                <br />
                {occurrence.country && (
                  <>
                    <strong>Country:</strong> {occurrence.country}
                    <br />
                  </>
                )}
                {occurrence.locality && (
                  <>
                    <strong>Locality:</strong> {occurrence.locality}
                    <br />
                  </>
                )}
                {occurrence.year && (
                  <>
                    <strong>Year:</strong> {occurrence.year}
                    <br />
                  </>
                )}
                <strong>Coordinates:</strong> {occurrence.decimalLatitude.toFixed(4)},{" "}
                {occurrence.decimalLongitude.toFixed(4)}
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
      <div className="text-center mt-2" style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
        Showing {occurrences.length} occurrence{occurrences.length !== 1 ? "s" : ""} with coordinates
      </div>
    </div>
  );
};
